import { useCanViewIdentificationStats } from '@app/hooks/useCanViewIdentificationStats';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Title,
  Icon,
  LinkTo,
  Toolbar,
  DropdownMenu,
  OptionMenu,
} from '@common/components';
import { LanguagePicker } from '@app/components/LanguagePicker';
import {
  faUser,
  faRightFromBracket,
  faBars,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '@auth/hooks';
import { useAppDispatch } from '@app/hooks';
import { useLogoutMutation } from '@auth/api/auth';
import { setErrorModal } from '@app/slices/modals';
import { logout } from '@auth/slices/auth';
import { useTranslation } from 'react-i18next';
import { getBaseUrlRefresh } from '@app/api';

import { DEFAULT_THEME } from '@app/constants/theme';
import headerClasses from './Header.module.scss';

export default function Header({ openDrawer }) {
  const { t } = useTranslation('app\\Header');

  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const [logoutMutation] = useLogoutMutation();
  const [userAvatar, setUserAvatar] = useState();

  const canViewIdentificationStats = useCanViewIdentificationStats();

  const theme = user?.theme || DEFAULT_THEME;

  useEffect(() => {
    setUserAvatar('');
    if (user?.settings?.avatar) {
      setUserAvatar(
        `url("${getBaseUrlRefresh(
          user?.settings?.avatar,
        )}"),  url("/img/placeholders/avatar_placeholder.png")`,
      );
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      const res = await logoutMutation();
      if (res) dispatch(logout());
    } catch (error) {
      dispatch(setErrorModal('Unauthorized'));
    }
  };

  return (
    <Toolbar
      className={headerClasses.header}
      logo={
        <div className={headerClasses.logoContainer}>
          {user ? (
            <Button
              suffix={<Icon icon={faBars} size="2x" />}
              onClick={openDrawer}
              className={headerClasses.deletePadding}
            />
          ) : null}

          <LinkTo href="/" className={headerClasses.logoLink}>
            <img
              alt="datalab-logo"
              src="/img/datalab-logo.svg"
              width={135}
              className={headerClasses.logo}
            />
            {theme.logoLarge && (
              <img
                alt="logo"
                src={theme.logoLarge}
                width={135}
                className={`${headerClasses.logo} ${headerClasses.logoClient}`}
              />
            )}
            <img
              alt="datalab-logo"
              src="/img/datalab-logo.svg"
              width={135}
              className={headerClasses.logoMedium}
            />
            {theme.logoMedium && (
              <img
                alt="logo"
                src={theme.logoMedium}
                width={28}
                height={28}
                className={`${headerClasses.logoMedium} ${headerClasses.logoClient}`}
              />
            )}
            <img
              alt="datalab-logo"
              src="/img/logo-mobile.png"
              width={22}
              height={22}
              className={headerClasses.logoMobile}
            />
            {theme.logoSmall && (
              <img
                alt="datalab-logo"
                src={theme.logoSmall}
                width={22}
                className={`${headerClasses.logoMobile} ${headerClasses.logoClient}`}
              />
            )}
          </LinkTo>
          {user && (
            <nav className={headerClasses.navTabs}>
              <DropdownMenu label={t('sectionStats')} href="/dashboard/data">
                <OptionMenu href="/dashboard/data/usage">
                  {t('optionUsage')}
                </OptionMenu>
                <OptionMenu href="/dashboard/data/results">
                  {t('optionResults')}
                </OptionMenu>
              </DropdownMenu>
              <DropdownMenu label={t('sectionNeuro')} href="/dashboard/neuro">
                <OptionMenu href="/dashboard/neuro/analysis">
                  {t('optionAnalysis')}
                </OptionMenu>
                <OptionMenu
                  className={!canViewIdentificationStats ? 'disabled' : ''}
                  href="/dashboard/neuro/identification"
                >
                  {t('optionIdentity')}
                </OptionMenu>
              </DropdownMenu>
              {user?.role.name === 'datalab-administrator' && (
                <LinkTo href="/dashboard/sync-monitor">
                  {t('sectionSyncMonitor')}
                </LinkTo>
              )}
            </nav>
          )}
        </div>
      }
    >
      <nav className={headerClasses.nav}>
        {user && (
          <div className={headerClasses.userLogged}>
            <Title
              className={headerClasses.buttonTitle}
              type="p"
              color="font-primary"
            >
              {user.first_name} {user.last_name}
            </Title>
            <div
              style={{ backgroundImage: userAvatar }}
              className={headerClasses.avatar}
            />
          </div>
        )}
        {user && (
          <div className={headerClasses.buttons}>
            <LinkTo
              button
              className={headerClasses.deletePadding}
              prefix={<Icon icon={faUser} />}
              href="/dashboard/user/profile"
            >
              <span className={headerClasses.buttonTitle}>
                {t('optionProfile')}
              </span>
            </LinkTo>
            <Button
              color="font-primary"
              className={headerClasses.deletePadding}
              prefix={<Icon icon={faRightFromBracket} />}
              onClick={() => handleLogout()}
            >
              <span className={headerClasses.buttonTitle}>
                {t('optionExit')}
              </span>
            </Button>
          </div>
        )}
        <LanguagePicker />
      </nav>
    </Toolbar>
  );
}

Header.propTypes = {
  openDrawer: PropTypes.func,
};
